/**
 * @fileoverview Factory for setting up common input handler patterns.
 * Reduces duplicated event listener code across searchers.
 * @module utils/InputHandlerFactory
 */

import { DelayedAction } from './DelayedAction.js';

const DELAY_MS_INPUTSEARCH_KEYPRESS = 250;


/**
 * @typedef {Object} InputHandlerConfig
 * @property {HTMLElement} inputEl - The input element (text input or select)
 * @property {HTMLElement} [clearBtnEl] - Optional clear button element
 * @property {Function} onSearch - Callback function (value, isEnterKey) => void
 * @property {string} [inputEvent='input'] - Event to listen for on input ('input' for text, 'change' for select)
 * @property {boolean} [selectOnFocus=true] - Whether to select all text on focus (text inputs only)
 * @property {boolean} [clearOnEscape=true] - Whether to clear input on Escape key
 */


/**
 * Sets up standardized input handlers for search inputs.
 * Handles input events, Enter key, focus selection, and clear button.
 *
 * @param {InputHandlerConfig} config - Configuration object
 * @returns {Function} Cleanup function to remove event listeners
 *
 */
export function setupSearchInput(config) {
    const {
        inputEl,
        clearBtnEl,
        onSearch,
        inputEvent = 'input',
        selectOnFocus = true,
        clearOnEscape = true,
        delayedKeyReaction = true
    } = config;

    if (!inputEl || !onSearch) {
        console.error('ERROR: setupSearchInput: inputEl and onSearch are required');
        return () => {};
    }

    const handlers = [];

    // Input/change handler with optional delay
    const delayedSearch = (delayedKeyReaction && DELAY_MS_INPUTSEARCH_KEYPRESS) ? new DelayedAction((value) => onSearch(value, false), DELAY_MS_INPUTSEARCH_KEYPRESS) : null;

    const inputHandler = (e) => {
        const isEnterKey = inputEvent === 'change'; // Select changes are like Enter
        if (isEnterKey) {
            // Select changes trigger immediately
            if (delayedSearch) delayedSearch.clear();
            onSearch(e.target.value, true);
        } else if (delayedKeyReaction && delayedSearch) {
            // Debounce (delay) regular typing - resets timer on each keystroke
            delayedSearch.schedule(e.target.value);
        } else {
            // No delay - trigger immediately
            onSearch(e.target.value, false);
        }
    };
    inputEl.addEventListener(inputEvent, inputHandler);
    handlers.push(() => {
        inputEl.removeEventListener(inputEvent, inputHandler);
        if (delayedSearch) delayedSearch.clear();
    });

    // Keydown handler for Enter and Escape
    const keydownHandler = (e) => {
        if (e.key === 'Enter') {
            if (delayedSearch) delayedSearch.clear();
            onSearch(e.target.value, true);
        } else if (e.key === 'Escape' && clearOnEscape) {
            if (delayedSearch) delayedSearch.clear();
            inputEl.value = '';
            onSearch('', false);
        }
    };
    inputEl.addEventListener('keydown', keydownHandler);
    handlers.push(() => inputEl.removeEventListener('keydown', keydownHandler));

    // Focus handler - select all text
    if (selectOnFocus && inputEl.type !== 'select-one') {
        const focusHandler = (e) => e.target.select();
        inputEl.addEventListener('focus', focusHandler);
        handlers.push(() => inputEl.removeEventListener('focus', focusHandler));
    }

    // Clear button handler
    if (clearBtnEl) {
        const clearHandler = () => {
            if (delayedSearch) delayedSearch.clear();
            inputEl.value = '';
            onSearch('', false);
            inputEl.focus();
        };
        clearBtnEl.addEventListener('click', clearHandler);
        handlers.push(() => clearBtnEl.removeEventListener('click', clearHandler));
    }

    // Return cleanup function
    return () => handlers.forEach(cleanup => cleanup());
}


/**
 * Sets up a text search input with standard behavior.
 * Convenience wrapper around setupSearchInput for text inputs.
 *
 * @param {HTMLElement} inputEl - The text input element
 * @param {HTMLElement} [clearBtnEl] - Optional clear button element
 * @param {Function} onSearch - Callback function (value, isEnterKey) => void
 * @returns {Function} Cleanup function
 */
export function setupTextSearchInput(inputEl, clearBtnEl, onSearch) {
    return setupSearchInput({
        inputEl,
        clearBtnEl,
        onSearch,
        inputEvent: 'input',
        selectOnFocus: true
    });
}


/**
 * Sets up a select dropdown with standard behavior.
 * Convenience wrapper around setupSearchInput for select elements.
 *
 * @param {HTMLSelectElement} selectEl - The select element
 * @param {HTMLElement} [clearBtnEl] - Optional clear button element
 * @param {Function} onSearch - Callback function (value, isEnterKey) => void
 * @returns {Function} Cleanup function
 */
export function setupSelectSearchInput(selectEl, clearBtnEl, onSearch) {
    return setupSearchInput({
        inputEl: selectEl,
        clearBtnEl,
        onSearch,
        inputEvent: 'change',
        selectOnFocus: false
    });
}
